import { storeName } from "../../emails/constants";
import type { StoreCode } from "../models/shopify/storeCodeSchema";
import type { OrderItemDTO, OrderItemGroupDTO } from "./orderResolver";

/**
 * 領収書フッタの「適格請求書発行事業者」情報。サバキュー (株式会社サバキュー) は適格請求書
 * 発行事業者として登録済み。両ストア (survaq / calqs) ともこの法人で発行する。
 *
 * 適格請求書の必須項目:
 *  - 適格請求書発行事業者の氏名 (法人名)
 *  - 適格請求書発行事業者の登録番号 (T + 13 桁)
 *  - 取引年月日 / 取引内容 / 取引金額 / 税率別の合計と消費税額 / 受領者の氏名
 */
export const COMPANY_INFO = {
  legalName: "株式会社サバキュー",
  /** インボイス制度の登録番号 (T + 13 桁) */
  invoiceRegistrationNumber: "T8180001142147",
  postalCode: "501-0115",
  address: "岐阜県岐阜市鏡島南2-5-6",
} as const;

/**
 * ストア表示名 (領収書ヘッダ用)。`emails/constants.ts` の `storeName.ja` を再利用して
 * 重複定義による乖離を防ぐ。
 */
export const STORE_DISPLAY_NAME: Record<StoreCode, string> = storeName.ja;

/** 領収書ヘッダのロゴ。public 配下の静的アセットを参照する */
export const storeLogo = (store: StoreCode): { src: string; alt: string } => ({
  src: store === "calqs" ? "/calqs-logo.svg" : "/logo.png",
  alt: STORE_DISPLAY_NAME[store],
});

/**
 * 領収書の発行日 (= 注文日 createdAt) を `YYYY/MM/DD` 形式に整形。
 * `formatDate` (ja-JP toLocaleDateString) は「2026年5月1日」になり領収書の慣例と異なるので別関数。
 */
export const formatReceiptDate = (iso: string): string => {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}/${m}/${day}`;
};

/**
 * 税込価格から内税分の消費税額を逆算する。
 * サバキュー運用は全商品 10% 税込価格表示 (Shopify `Taxes included in prices` ON)。
 * 軽減税率商品が出てきたら税率を引数に。
 *
 * NOTE: 行ごとに呼ぶと 1 円単位の累積誤差が `taxJpy` (注文単位の真実) とズレるため、
 * 領収書では「税率別の合計」レベルでのみ使用し、行ごとの内税表示は出さない。
 */
export const computeTaxIncluded10Pct = (priceIncTaxJpy: number): number =>
  Math.round(priceIncTaxJpy / 11);

/**
 * 領収書テーブル用に bundle / single を平坦化する。
 *  - single: その単品行
 *  - bundle: 親 + 子を全部別行で展開 (Cart Transform Function で親子に按分された
 *    `originalUnitPrice` を持つ前提)
 *
 * 注文詳細 (`order-detail.tsx`) は bundle を grouping して 1 セットに見せる UX 重視だが、
 * 領収書は会計書類として line item ごとに価格・数量を 1 行ずつ並べる方が透明性が高い。
 */
export const flattenItemsForReceipt = (groups: OrderItemGroupDTO[]): OrderItemDTO[] => {
  const out: OrderItemDTO[] = [];
  for (const g of groups) {
    if (g.kind === "single") {
      out.push(g.item);
    } else {
      out.push(g.parent);
      for (const c of g.children) out.push(c);
    }
  }
  return out;
};
